// Mieterportal (Portale E4/E5): Übersicht, Meldungen, Schaden melden, Termine, Dokumente, Mietkonto.
// Daten nur über die Portal-Funktionen (lib/portal/mieterApi); geschrieben wird nur „Schaden melden“.
import { useCallback, useEffect, useState } from 'react';
import { CalendarBlank, ChatCircleDots, CircleNotch, FolderSimple, HouseLine, Plus, SquaresFour, Wallet } from '@phosphor-icons/react';
import PortalRahmen, { usePortalTab } from '../../components/portal/PortalRahmen';
import BereichFolgt from '../../components/portal/BereichFolgt';
import { FehlerHinweis } from '../../components/portal/bausteine';
import { mieterTabs, type MieterTab } from '../../lib/portal/tabs';
import { meldungStufe, type Meldung, type MieterKontext } from '../../lib/portal/mieter';
import { ladeMeineDokumente, ladeMeineMeldungen, ladeMeineTermine, ladeMieterKontext } from '../../lib/portal/mieterApi';
import type { PortalDokument, PortalTermin } from '../../lib/portal/mieterE5';
import MieterTermine from './MieterTermine';
import MieterDokumente from './MieterDokumente';
import MieterMietkonto from './MieterMietkonto';
import type { PortalProfil } from '../../components/portal/PortalWeiche';
import MieterUebersicht from './MieterUebersicht';
import MieterMeldungen, { type MeldungFilter } from './MieterMeldungen';
import SchadenMeldenSheet from './SchadenMeldenSheet';
import AnfrageSheet from '../../components/portal/AnfrageSheet';
import { stelleAnfrage } from '../../lib/portal/nachrichtenApi';

const SYMBOLE = { uebersicht: SquaresFour, meldungen: ChatCircleDots, termine: CalendarBlank, dokumente: FolderSimple, mietkonto: Wallet, haus: HouseLine };

const FOLGT: Partial<Record<MieterTab, { oben: string; titel: string }>> = {
  haus: { oben: 'Ihr Haus', titel: 'Rund ums Haus' },
};

export default function MieterPortal({ profil }: { profil: PortalProfil }) {
  // Haus-Tab erst, wenn es Hausdaten gibt (Entscheidung 22.10.2026: ohne Daten ausblenden)
  const tabs = mieterTabs({ haus: false });
  const [tab, setTab] = usePortalTab(tabs, '/mieterportal');
  const [kontext, setKontext] = useState<MieterKontext | null>(null);
  const [meldungen, setMeldungen] = useState<Meldung[]>([]);
  const [termine, setTermine] = useState<PortalTermin[]>([]);
  const [dokumente, setDokumente] = useState<PortalDokument[]>([]);
  const [laedt, setLaedt] = useState(true);
  const [melden, setMelden] = useState(false);
  const [anfrage, setAnfrage] = useState(false);
  const [filter, setFilter] = useState<MeldungFilter>('offen');
  const [neuId, setNeuId] = useState<string | null>(null);
  const [jetzt] = useState(() => new Date());

  const laden = useCallback(async () => {
    const [k, m, te, d] = await Promise.all([ladeMieterKontext(), ladeMeineMeldungen(), ladeMeineTermine(), ladeMeineDokumente()]);
    setKontext(k);
    setMeldungen(m);
    setTermine(te);
    setDokumente(d);
    setLaedt(false);
  }, []);
  useEffect(() => { void laden(); }, [laden]);

  const name = profil.full_name || [kontext?.mieter.vorname, kontext?.mieter.nachname].filter(Boolean).join(' ') || 'Mieter';
  const strasse = [kontext?.objekt?.strasse, kontext?.objekt?.hausnummer].filter(Boolean).join(' ');
  const einheit = kontext?.einheit?.bezeichnung || kontext?.mieter.einheit_text || '';
  const adresse = [strasse, einheit].filter(Boolean).join(' · ');
  const ort = [strasse, [kontext?.objekt?.plz, kontext?.objekt?.stadt].filter(Boolean).join(' ')].filter(Boolean).join(', ');
  const offen = meldungen.filter((m) => meldungStufe(m) < 3).length;

  const inhalt = () => {
    if (laedt) {
      return (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '80px 0', color: 'var(--p-w40)' }} aria-busy="true" aria-label="Lädt">
          <CircleNotch size={24} weight="bold" className="animate-spin" />
        </div>
      );
    }
    if (!kontext) {
      return <FehlerHinweis text="Ihre Wohnungsdaten konnten nicht geladen werden. Bitte versuchen Sie es später noch einmal oder wenden Sie sich an die Verwaltung." />;
    }
    if (tab === 'uebersicht') {
      return <MieterUebersicht kontext={kontext} meldungen={meldungen} termine={termine} ort={ort} jetzt={jetzt} onMelden={() => setMelden(true)} onAnfrage={() => setAnfrage(true)} onTab={(t) => setTab(t)} />;
    }
    if (tab === 'meldungen') return <MieterMeldungen meldungen={meldungen} filter={filter} onFilter={setFilter} neuId={neuId} onAnfrage={() => setAnfrage(true)} />;
    if (tab === 'termine') return <MieterTermine termine={termine} ort={ort} onMeldung={() => setTab('meldungen')} />;
    if (tab === 'dokumente') return <MieterDokumente dokumente={dokumente} />;
    if (tab === 'mietkonto') return <MieterMietkonto kontext={kontext} jetzt={jetzt} />;
    const f = FOLGT[tab];
    return f ? <BereichFolgt {...f} /> : null;
  };

  return (
    <PortalRahmen
      portal="Mieterportal"
      tabs={tabs}
      symbole={SYMBOLE}
      aktiv={tab}
      onTab={setTab}
      anzahl={{ meldungen: tab === 'meldungen' ? 0 : offen }}
      profil={{ name, email: profil.email ?? '' }}
      profilTip={adresse ? `${name} · ${adresse}` : name}
      aktion={kontext ? (
        <button type="button" className="p-knopf p-knopf-gold mp-cta" onClick={() => setMelden(true)}
          data-tip="Einen Schaden oder Mangel an die Verwaltung melden" aria-label="Schaden melden" style={{ height: 40, borderRadius: 20, fontSize: 13 }}>
          <Plus size={16} weight="bold" /><span className="mp-cta-text">Schaden melden</span>
        </button>
      ) : undefined}
    >
      {inhalt()}
      {melden && (
        <SchadenMeldenSheet
          adresse={adresse}
          onSchliessen={() => setMelden(false)}
          onGesendet={(id) => { setNeuId(id); setFilter('offen'); void laden(); }}
          onZuMeldungen={() => { setMelden(false); setFilter('offen'); setTab('meldungen'); }}
        />
      )}
      {anfrage && (
        <AnfrageSheet unterzeile={adresse} listeName="Meldungen"
          senden={(b, t) => stelleAnfrage(b, t)}
          onSchliessen={() => setAnfrage(false)}
          onGesendet={() => { setFilter('offen'); void laden(); }}
          onZuListe={() => { setAnfrage(false); setFilter('offen'); setTab('meldungen'); }} />
      )}
    </PortalRahmen>
  );
}
